/**
 * Which strategy holds the money, and which one is actually earning on it.
 *
 * The account runs two strategies for as long as the migration takes, and
 * nothing on this screen said how the capital divided between them. The cycle
 * counts were visible and the cycle counts are the misleading half: the older
 * strategy held four fifths of the exposure and the whole of the unrealised
 * loss while closing a cycle a day, and the newer one turned over a fifth of
 * the capital several times in the same day. Reading that took hand-written
 * SQL, which is the definition of a screen not answering its question.
 *
 * Two halves per strategy, and the divider between them is load-bearing:
 *
 *   - Above it, the open book — point-in-time, no period control over it, per
 *     §11. A strategy looks excellent here by never closing a loser.
 *   - Below it, the last twenty-four hours closed. Fixed at twenty-four hours
 *     whatever the control above is set to, and labelled as such: a comparison
 *     whose window moves under the reader is one nobody can repeat.
 *
 * The bar carries the accent, and only one segment of it does — the strategy
 * the account is migrating to. That is §2 v6's rule for the allocation bar
 * applied to the question this block asks: how far the migration has got. The
 * figures themselves are ranked by size, never by the accent, because green
 * and red mean money here and amber never states a result.
 *
 * Every figure arrived measured. The shares, the ages and the net per
 * capital-day are all computed on the server; this file renders strings.
 */
import { Stat } from "@/design-system/Stat";
import { Metric } from "@/design-system/Metric";
import type { StrategyMigrationResponse, StrategyStandingResponse } from "@/shared/api";
import { ABSENT, count, percent } from "@/shared/metric/metric";
import {
  barWidth,
  isZero,
  shifted,
  signedMoney,
  signedPercent,
} from "@/shared/money/money";
import { generationOfVersion, versionLabel } from "@/shared/strategy/versions";
import { humanAge } from "@/shared/time/age";
import { PANEL_TITLE } from "@/design-system/Panel";

/**
 * How old the open cycles are, in one line.
 *
 * The median and the oldest together, because either alone lies in a way this
 * account has already been bitten by: a book whose median age is twenty hours
 * and whose oldest cycle is ten days is a working strategy with one stuck
 * position, and a book whose median is five days is a strategy that has
 * stopped closing. A single count of cycles past a threshold reads the same
 * in both cases.
 */
function ageing(standing: StrategyStandingResponse): string | null {
  const { median_age_seconds: median, oldest_age_seconds: oldest } = standing;
  if (median === null || oldest === null) return null;
  return `медиана ${humanAge(median)} · старейшему ${humanAge(oldest)}`;
}

function Standing({
  standing,
  realisedKnown,
}: {
  readonly standing: StrategyStandingResponse;
  /**
   * Whether the last day could be read at all.
   *
   * When it could not, the three figures under the rule are unknown rather
   * than zero — and zero is the one thing they must not be shown as, since a
   * strategy that closed nothing all day and one whose day could not be
   * counted lead to opposite conclusions.
   */
  readonly realisedKnown: boolean;
}): React.JSX.Element {
  const age = ageing(standing);
  const velocity = realisedKnown ? standing.net_per_capital_day : null;
  return (
    <div
      data-testid={`migration-standing-${standing.strategy_version}`}
      className="flex min-w-0 flex-col gap-2 rounded-panel border border-border-subtle bg-surface-hover p-3"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-label-caps text-text-primary">
          {versionLabel(standing.strategy_version)}
        </span>
        <span
          className="font-numeric text-helper-sm tabular-nums text-text-muted"
          title="Доля всего открытого объёма, которую держит эта стратегия"
        >
          {percent(standing.share_of_notional, 0)} капитала
        </span>
      </div>

      {/* Two columns, both figures ranged on the same line. Label left, value
          right, one grid for the pair — which is what keeps the numbers in a
          column instead of stepping down the card. */}
      <div className="grid grid-cols-2 gap-2">
        <Stat
          label="В позициях"
          value={
            <Metric
              metricId="migration.notional"
              role="detail"
              chrome="figure"
              value={standing.notional}
            />
          }
          note={`${count(standing.open_cycles)} циклов`}
        />
        <Stat
          label="Нереализовано"
          value={
            <Metric
              metricId="migration.unrealised"
              role="detail"
              chrome="figure"
              value={standing.unrealised}
              directional
            />
          }
          note={age ?? "открытых циклов нет"}
        />
      </div>

      {/* The rule that separates a state from a result. Above it, what the
          strategy is holding right now; below it, what it did in a window with
          a stated length. Without the divider the six figures read as one set
          measured the same way, and they are not. */}
      <div className="border-t border-border-subtle pt-2">
        <div className="flex items-end justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span
              className="text-helper-sm text-text-muted"
              title="Чистый результат закрытых за сутки циклов, делённый на капитал, умноженный на время, которое он был занят. Читается как дневная ставка на занятый капитал."
            >
              доход в день на капитал
            </span>
            {velocity === null ? (
              // Not a zero. A strategy that closed nothing has no velocity,
              // and printing 0.00 would report it as one that worked all day
              // for nothing.
              <span
                data-testid={`migration-velocity-${standing.strategy_version}`}
                className="font-numeric text-metric-md tabular-nums text-text-secondary"
                title={
                  realisedKnown
                    ? "За последние сутки эта стратегия не закрыла ни одного цикла"
                    : "Историю за сутки прочитать не удалось"
                }
              >
                {ABSENT}
              </span>
            ) : (
              <Metric
                metricId="migration.net_per_capital_day"
                role="detail"
                chrome="figure"
                size="large"
                // The percentage, not the fraction. The direction and the exact
                // figure on hover are both read from the value, so handing it
                // the fraction while showing a percentage would colour a rate
                // of 0.4 % a day as an unchanged zero.
                value={shifted(velocity, 2)}
                display={signedPercent(velocity, 1)}
                directional
              />
            )}
          </div>
          <div className="flex flex-col items-end gap-0.5 text-right">
            <span className="text-helper-sm text-text-muted">За 24 ч</span>
            <span className="font-numeric text-body-main tabular-nums text-text-secondary">
              {realisedKnown ? `${count(standing.closed_cycles)} закрыто` : ABSENT}
            </span>
            <span
              data-testid={`migration-realised-${standing.strategy_version}`}
              data-metric-id="migration.realised_net"
              data-metric-role="detail"
              className={`font-numeric text-body-main tabular-nums ${
                isZero(standing.realised_net)
                  ? "text-text-secondary"
                  : standing.realised_net.startsWith("-")
                    ? "text-status-negative"
                    : "text-status-positive"
              }`}
              title={standing.realised_net}
            >
              {realisedKnown ? signedMoney(standing.realised_net) : ABSENT}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MigrationPanel({
  migration,
}: {
  readonly migration: StrategyMigrationResponse;
}): React.JSX.Element | null {
  // One strategy is not a migration. The block appears when the account is
  // actually running two and disappears the day it finishes running one —
  // rather than standing there afterwards reporting a completed move forever.
  if (migration.standings.length < 2) return null;

  return (
    <section
      aria-labelledby="migration-heading"
      data-overview-region="migration"
      data-testid="migration-panel"
      className="flex flex-col gap-3 rounded-panel border border-border-subtle bg-surface-default p-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="migration-heading" className={PANEL_TITLE}>
          Стратегии счёта
        </h2>
        <span className="text-helper-sm text-text-muted">
          Позиции — на текущий момент. Результат — за последние 24 часа, независимо
          от периода ниже.
        </span>
      </div>

      {/* The split, as a shape. One bar rather than two percentages: how far
          the migration has got is a proportion before it is a figure, and the
          accent on the arriving strategy is the one place this block spends
          colour that is not money. */}
      <span
        aria-hidden="true"
        data-testid="migration-bar"
        className="flex h-3 w-full overflow-hidden rounded-row border border-border-subtle bg-surface-hover"
      >
        {migration.standings.map((standing) => (
          <span
            key={standing.strategy_version}
            data-testid={`migration-segment-${standing.strategy_version}`}
            title={`${versionLabel(standing.strategy_version)}: ${percent(
              standing.share_of_notional,
              0,
            )} капитала`}
            className={`block h-full ${
              generationOfVersion(standing.strategy_version) === "v2"
                ? "bg-capital-positions"
                : "bg-capital-recovery"
            }`}
            style={{ width: barWidth(standing.share_of_notional) }}
          />
        ))}
      </span>

      <div className="grid min-w-0 grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
        {migration.standings.map((standing) => (
          <Standing
            key={standing.strategy_version}
            standing={standing}
            realisedKnown={!migration.truncated}
          />
        ))}
      </div>

      {migration.truncated && (
        // Said rather than shown quietly: the block's whole value is that the
        // two halves can be compared, and a partial sum printed as a total
        // would break exactly that.
        <p className="text-helper-sm text-status-warning" data-testid="migration-partial">
          За сутки закрыто больше циклов, чем можно прочитать за один запрос.
          Результат за 24 часа не показан — полная история на экране «Циклы».
        </p>
      )}
    </section>
  );
}
